/**
 * Public /projects — blog-style index of work the broker has finished or
 * has in flight. Filter chips at the top; cards clickable through to the
 * per-project detail page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Wrench, ArrowUpRight, Github, ExternalLink } from "lucide-react";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";
import { fetchProjects } from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All work" },
  { key: "finished", label: "Finished" },
  { key: "ongoing", label: "Ongoing" },
];

export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProjects(filter === "all" ? null : filter)
      .then((r) => setProjects(r.items || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <>
      <Seo
        title="Projects · CasperDomains"
        description="Notes and case studies on the tools, experiments, and side projects built alongside the domain brokerage."
        type="website"
        siteName="CasperDomains"
      />
      <SiteHeader />
      <main
        style={{
          fontFamily: "var(--cd-font)",
          background: "var(--cd-white)",
          minHeight: "70vh",
          paddingBottom: 120,
        }}
        data-testid="projects-page"
      >
        <div className="cd-container" style={{ paddingTop: 72 }}>
          <div style={{ maxWidth: 720 }}>
            <span className="cd-eyebrow" data-testid="projects-eyebrow">
              Portfolio · What I ship
            </span>
            <h1
              className="cd-display"
              style={{ marginTop: 12, letterSpacing: "-0.03em" }}
              data-testid="projects-title"
            >
              Projects, playbooks, and works-in-progress.
            </h1>
            <p style={{ marginTop: 20, fontSize: 18, lineHeight: 1.6, color: "var(--cd-gray-600)" }}>
              A running log of the domain deals, tooling, and side-quests I'm building
              alongside the brokerage. Some shipped, some still cooking. The honest
              messy record, not a curated résumé.
            </p>
          </div>

          <div className="cd-projects__filters" data-testid="projects-filters">
            {FILTERS.map((f) => (
              <button
                type="button"
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`projects-filter-${f.key}`}
                className={`cd-projects__filter ${filter === f.key ? "is-active" : ""}`}
              >
                {f.label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: "var(--cd-gray-500)", letterSpacing: ".05em", textTransform: "uppercase", fontWeight: 600 }}>
              {loading ? "Loading…" : `${projects.length} project${projects.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {!loading && projects.length === 0 && (
            <div
              data-testid="projects-empty"
              style={{
                marginTop: 48,
                padding: "60px 30px",
                textAlign: "center",
                border: "1px dashed var(--cd-gray-200)",
                borderRadius: 20,
                color: "var(--cd-gray-500)",
              }}
            >
              <Wrench size={28} strokeWidth={1.5} style={{ margin: "0 auto 14px", color: "var(--cd-blue-500)" }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--cd-navy-950)" }}>
                Nothing published in this bucket yet.
              </div>
              <div style={{ marginTop: 6, fontSize: 14 }}>
                {filter === "ongoing" ? "The next in-flight write-up will land here." : "Check back soon. New work drops every few weeks."}
              </div>
            </div>
          )}

          <div className="cd-projects__grid" data-testid="projects-grid">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} />
            ))}
          </div>
        </div>
      </main>
      <SiteFooter />
      <BullseyeEmbed page="projects" />
    </>
  );
}

function ProjectCard({ p }) {
  const isFinished = p.status === "finished";
  return (
    <Link
      to={`/projects/${p.slug}`}
      className="cd-project-card"
      data-testid={`project-card-${p.slug}`}
    >
      <div
        className="cd-project-card__cover"
        style={{
          backgroundImage: p.cover_url
            ? `url(${p.cover_url})`
            : `linear-gradient(135deg, #03142f, #0a2456 60%, #4b6fff)`,
          backgroundPosition: p.cover_focal
            ? `${p.cover_focal.x}% ${p.cover_focal.y}%`
            : "center center",
        }}
      >
        {!p.cover_url && (
          <span className="cd-project-card__cover-mono">{initials(p.title)}</span>
        )}
        <span className={`cd-project-card__status ${isFinished ? "is-finished" : "is-ongoing"}`}>
          {isFinished ? <Rocket size={11} strokeWidth={2.4} /> : <Wrench size={11} strokeWidth={2.4} />}
          {isFinished ? "Shipped" : "In flight"}
        </span>
      </div>
      <div className="cd-project-card__body">
        <h2 className="cd-project-card__title">{p.title}</h2>
        {p.summary && (
          <p className="cd-project-card__summary">{p.summary}</p>
        )}
        {p.tech_tags?.length > 0 && (
          <div className="cd-project-card__tags">
            {p.tech_tags.slice(0, 4).map((t) => (
              <span key={t} className="cd-project-card__tag">{t}</span>
            ))}
            {p.tech_tags.length > 4 && (
              <span className="cd-project-card__tag" style={{ opacity: 0.6 }}>+{p.tech_tags.length - 4}</span>
            )}
          </div>
        )}
        <div className="cd-project-card__cta">
          <span>Read write-up</span>
          <ArrowUpRight size={14} strokeWidth={2.4} />
        </div>
      </div>
    </Link>
  );
}

function initials(s) {
  return (s || "")
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}
